import { Period, PERIODS } from "@/shared/domain/metrics";

import { BucketWindow } from "./bucketWindow";

/**
 * URL search-param keys that encode an open drill-down dialog. Persisting the
 * selection in the URL (rather than component state) lets a user click through
 * to a session and return — via browser back — to the same open drill-down.
 */
export const DRILLDOWN_PARAMS = {
  metric: "drilldownMetric",
  start: "drilldownStart",
  end: "drilldownEnd",
  period: "drilldownPeriod",
} as const;

export interface DrilldownSelection {
  metricName: string;
  bucket: BucketWindow;
  /** Period the user drilled at (for the dialog title). `null` if absent or
   *  unrecognized in the URL — callers fall back to the current period. */
  period: Period | null;
}

function asPeriod(value: string | null): Period | null {
  return value !== null && (PERIODS as readonly string[]).includes(value)
    ? (value as Period)
    : null;
}

/**
 * Parse drill-down parameters out of the current search params. Returns `null`
 * when no (valid) drill-down is encoded, which the caller treats as "dialog
 * closed".
 */
export function readDrilldownParams(
  params: URLSearchParams,
): DrilldownSelection | null {
  const metricName = params.get(DRILLDOWN_PARAMS.metric);
  const start = params.get(DRILLDOWN_PARAMS.start);
  const end = params.get(DRILLDOWN_PARAMS.end);
  if (!metricName || start === null || end === null) {
    return null;
  }
  const startNum = Number(start);
  const endNum = Number(end);
  if (!Number.isFinite(startNum) || !Number.isFinite(endNum)) {
    return null;
  }
  return {
    metricName,
    bucket: { start_time: startNum, end_time: endNum },
    period: asPeriod(params.get(DRILLDOWN_PARAMS.period)),
  };
}

/** Return a copy of `prev` with the drill-down selection encoded, preserving
 *  any unrelated params. */
export function setDrilldownParams(
  prev: URLSearchParams,
  selection: { metricName: string; bucket: BucketWindow; period: Period },
): URLSearchParams {
  const next = new URLSearchParams(prev);
  next.set(DRILLDOWN_PARAMS.metric, selection.metricName);
  next.set(DRILLDOWN_PARAMS.start, String(selection.bucket.start_time));
  next.set(DRILLDOWN_PARAMS.end, String(selection.bucket.end_time));
  next.set(DRILLDOWN_PARAMS.period, selection.period);
  return next;
}

/** Return a copy of `prev` with all drill-down params removed, preserving any
 *  unrelated params. */
export function clearDrilldownParams(prev: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(prev);
  for (const key of Object.values(DRILLDOWN_PARAMS)) {
    next.delete(key);
  }
  return next;
}
